import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import QuestionInput from '../components/QuestionInput';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export function StepQuestions({ 
  title, 
  description, 
  questions, 
  answers, 
  onAnswerChange, 
  onNext, 
  onBack,
  isFirst = false 
}) {
  const [showErrors, setShowErrors] = useState(false);

  const handleAnswerChange = (questionId, value) => {
    onAnswerChange(questionId, value);
  };

  // Get visible questions (considering conditionals - showIf and showIfNot)
  const getVisibleQuestions = () => {
    return questions.filter(question => {
      // No conditional - always show
      if (!question.conditional) {
        return true;
      }
      
      const condValue = answers[question.conditional];
      
      // Handle showIfNot - hide if value matches (for multiselect, check if array includes the value)
      if (question.showIfNot !== undefined) {
        const showIfNot = question.showIfNot;
        
        // For multiselect answers (arrays)
        if (Array.isArray(condValue)) {
          // Hide if the "not" value is selected
          return !condValue.includes(showIfNot);
        }
        
        // For single values
        return condValue !== showIfNot;
      }
      
      // Handle showIf - show only if value matches
      if (question.showIf !== undefined) {
        const showIf = Array.isArray(question.showIf) 
          ? question.showIf 
          : [question.showIf];
        
        // For multiselect answers
        if (Array.isArray(condValue)) {
          return showIf.some(s => condValue.includes(s));
        }
        
        return showIf.includes(condValue);
      }
      
      return true;
    });
  };

  const visibleQuestions = getVisibleQuestions();

  // Check if all visible questions are answered
  const validateAnswers = () => {
    const unanswered = visibleQuestions.filter(q => {
      const answer = answers[q.id];
      if (answer === undefined || answer === null || answer === '') return true;
      if (Array.isArray(answer) && (answer.length === 0 || answer.every(v => !v))) return true;
      return false;
    });

    return unanswered;
  };

  const handleNext = () => {
    const unanswered = validateAnswers();
    
    if (unanswered.length > 0) {
      setShowErrors(true);
      toast.error(`Por favor completá todas las preguntas (${unanswered.length} sin responder)`);
      
      // Scroll to first unanswered question
      const firstUnanswered = document.querySelector(`[data-testid*="${unanswered[0].id}"]`);
      if (firstUnanswered) {
        firstUnanswered.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    setShowErrors(false);
    onNext();
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
          <p className="text-xs text-muted-foreground mt-2">
            Todas las preguntas son obligatorias. Podés usar el micrófono 🎤 para dictar tus respuestas.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {visibleQuestions.map((question) => (
            <QuestionInput
              key={question.id}
              question={question}
              value={answers[question.id]}
              onChange={handleAnswerChange}
              showError={showErrors}
            />
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-between gap-4">
        {!isFirst && (
          <Button variant="outline" onClick={onBack} data-testid="wizard-back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Atrás
          </Button>
        )}
        <Button 
          onClick={handleNext} 
          className={isFirst ? 'w-full' : 'flex-1'}
          data-testid="wizard-next-button"
        >
          Siguiente
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

export default StepQuestions;
